<script setup>
import { computed, ref, watchEffect, onMounted } from 'vue'
import { useLocaleStore } from '@/stores/localeStore'

const emit = defineEmits()

const props = defineProps({
  entities: {
    type: Array,
    required: true
  }
})

const localEntities = ref([])
const iconPositions = ref({})
const draggedIcon = ref(null)
const dragOffset = ref({ x: 0, y: 0 })
const isDragging = ref(false)

// Save positions to localStorage
const savePositions = () => {
  localStorage.setItem('desktopIconPositions', JSON.stringify(iconPositions.value))
}

// Load saved positions from localStorage and set up drag listeners
onMounted(() => {
  const savedPositions = localStorage.getItem('desktopIconPositions')
  if (savedPositions) {
    try {
      const loaded = JSON.parse(savedPositions)
      iconPositions.value = loaded
    } catch (e) {
      console.error('Error loading desktop icon positions:', e)
    }
  }
  
  // Initialize default positions for icons that don't have saved positions
  // Games go on the right side, other apps on the left side
  const games = ['minesweeper', 'gorillas', 'stickHero', 'chess']
  let leftColumnIndex = 0
  let rightColumnIndex = 0
  
  desktopEntities.value.forEach((entity) => {
    if (!iconPositions.value[entity.id]) {
      if (games.includes(entity.id)) {
        // Place games on the right side, starting from top
        iconPositions.value[entity.id] = {
          x: window.innerWidth - 104, // 80px icon width + 24px margin from right
          y: 24 + (rightColumnIndex * 80) // 24px top padding + 80px spacing per icon
        }
        rightColumnIndex++
      } else {
        // Place other apps on the left side
      iconPositions.value[entity.id] = {
          x: 24, // Default X position, 24px from left
          y: 24 + (leftColumnIndex * 80) // 24px top padding + 80px spacing per icon
      }
        leftColumnIndex++
      }
    }
  })
  
  savePositions()
  
  // Add global event listeners for dragging
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
})

watchEffect(() => {
  localEntities.value = [...props.entities]
})

// Define the desired order of icons
const iconOrder = ['myProjects', 'myCV', 'contact', 'music', 'notepad', 'minesweeper', 'gorillas', 'stickHero', 'chess', 'chrome', 'photoshop', 'chatgpt']

const desktopEntities = computed(() => {
  const entities = localEntities.value.filter((entity) => entity.onDesktop)
  // Sort by the specified order
  return entities.sort((a, b) => {
    const indexA = iconOrder.indexOf(a.id)
    const indexB = iconOrder.indexOf(b.id)
    // If not in order array, put at end
    if (indexA === -1) return 1
    if (indexB === -1) return -1
    return indexA - indexB
  })
})

const toggleEffect = (selectedEntity) => {
  if (isDragging.value) return
  localEntities.value.forEach((entity) => {
    entity.isActive = entity.id === selectedEntity.id
  })
}

const removeFilterAndToggle = (entity) => {
  if (isDragging.value) return
  localEntities.value.forEach((e) => {
    e.isActive = false
  })
  emit('toggle-' + entity.id) // Emit on double click or single tap.
}

// Drag functionality
const startDrag = (e, entity) => {
  // Don't start drag on double-click
  if (e.detail === 2) return
  
  e.preventDefault()
  e.stopPropagation()
  isDragging.value = true
  draggedIcon.value = entity.id
  
  const currentPos = iconPositions.value[entity.id] || { x: 24, y: 0 }
  
  // Track both X and Y offsets for free movement
  dragOffset.value = {
    x: e.clientX - currentPos.x,
    y: e.clientY - currentPos.y
  }
  
  // Prevent text selection during drag
  document.body.style.userSelect = 'none'
}

const onDrag = (e) => {
  if (!isDragging.value || !draggedIcon.value) return
  
  const newX = e.clientX - dragOffset.value.x
  const newY = e.clientY - dragOffset.value.y
  
  // Keep icon within desktop bounds
  // Account for icon width (80px) and taskbar at bottom (~32px)
  const maxX = window.innerWidth - 80 // Icon width
  const maxY = window.innerHeight - 132 // 100px icon height + 32px taskbar
  
  // Allow free movement within desktop bounds
  iconPositions.value[draggedIcon.value] = {
    x: Math.max(0, Math.min(newX, maxX)),
    y: Math.max(0, Math.min(newY, maxY))
  }
}

const stopDrag = () => {
  if (isDragging.value) {
    isDragging.value = false
    savePositions()
    draggedIcon.value = null
    document.body.style.userSelect = ''
  }
}

// Detect if the user is on a mobile device
const isMobile = ref(false)

if (typeof window !== 'undefined') {
  isMobile.value = /Mobi|Android/i.test(navigator.userAgent)
}

// Locale management
const localeStore = useLocaleStore()

// Computed property to get the localized title
const getLocalizedTitle = (entity) => {
  return entity.title[localeStore.currentLocale] || entity.title['de'] || entity.title['en']
}

// If click occurs somewhere else, remove the active state
window.addEventListener('click', (e) => {
  if (!e.target.closest('.desktop-icons-container')) {
    localEntities.value.forEach((entity) => {
      entity.isActive = false
    })
  }
})
</script>

<template>
  <section class="absolute top-0 left-0 w-full h-full desktop-icons-container">
    <div
      v-for="entity in desktopEntities"
      :key="entity.id"
      class="absolute flex flex-col gap-2 items-center cursor-pointer select-none"
      :style="{
        left: `${iconPositions[entity.id]?.x || 24}px`,
        top: `${iconPositions[entity.id]?.y || 0}px`,
        width: '80px',
        transform: draggedIcon === entity.id && isDragging ? 'scale(1.1)' : 'scale(1)',
        transition: draggedIcon === entity.id && isDragging ? 'none' : 'transform 0.1s ease',
        zIndex: draggedIcon === entity.id ? 1000 : 1
      }"
      @mousedown="startDrag($event, entity)"
      @click.stop="isMobile ? removeFilterAndToggle(entity) : toggleEffect(entity)"
      @dblclick.stop="!isMobile && removeFilterAndToggle(entity)"
      :class="{ active: entity.isActive }"
    >
      <img
        class="w-11 h-11 pointer-events-none"
        :style="{
          ...entity.imageStyle,
          filter: entity.isActive ? 'opacity(0.5)' : 'opacity(1)'
        }"
        :src="entity.imgSrc"
        :alt="getLocalizedTitle(entity)"
        draggable="false"
      />
      <p
        class="text-white text-xs font-normal py-px px-1 text-center pointer-events-none"
        :style="{
          ...entity.textStyle,
          backgroundColor: entity.isActive ? '#0B61FF' : 'transparent',
          textShadow: entity.isActive ? 'none' : '0px 1px 1px rgba(1, 1, 1, 1), 0px 0px 4px #000'
        }"
      >
        {{ getLocalizedTitle(entity) }}
      </p>
    </div>
  </section>
</template>

<style scoped>
.active {
  outline: 1px dashed #fff;
  outline-offset: 2px;
}
</style>
